import React, { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from "react-redux";
import { Editor } from 'ckeditor5-custom-build-light/build/ckeditor';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import { getJournalEntries, clearJournalEntry, postJournalEntry, getUserReviews } from '../store/journal/thunks';
import { formatEmotionalState } from '../utils/reviewLabels';
import '../styles/summary.css';
import '../styles/logtrade.css';
import '../styles/filter.css';
import '../styles/journal.css';
import moment from 'moment';
import 'moment-timezone';
import { returnInTZ } from '../utils/timezone';
import JournalSkeleton from './JournalSkeleton';
import {
  Flex,
  Text,
  Center,
  Spinner,
  Input,
  useColorMode,
  Button,
  Box,
  Badge,
  HStack,
  VStack,
  Image,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  IconButton,
  Tooltip
} from "@chakra-ui/react";
import { FaImage } from "react-icons/fa";
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";


import useToastNotification from "../hooks/useToastNotification";
import useAsyncState from "../hooks/useAsyncState";

export default function Journal({ user }) {
  const dispatch = useDispatch();
  const showToast = useToastNotification();
  const { journalentries } = useSelector((state) => state.journal);
  const { loading: authLoading, success, info } = useAsyncState({
    onError: (info) => showToast(info?.response?.data?.result, { status: "error" }),
  });
  //const hasEntries = ((journalentries && journalentries.entries && Object.keys(journalentries.entries).length > 0) ? (true):(false));
  const hasEntries = ((journalentries && journalentries?.entries) ? (true):(false));


  const [backPageEnable, setBackPageEnable] = useState(false);
  const [nextPageEnable, setNextPageEnable] = useState(false);


  const [saveLoading, setSaveLoading] = useState(false);


  const [isEdit, setIsEdit] = useState(false);

  const { colorMode } = useColorMode();

  const [entryy, setEntry] = useState("");

  const today = returnInTZ(new Date().toISOString());
  const [currentmonth, setCurrentMonth] = useState(moment(today).month());
  const [currentyear, setCurrentYear] = useState(moment(today).year());


  const [selected_date, setSelectedDate] = useState(today);
  const [screenshotModalUrl, setScreenshotModalUrl] = useState(null);
  const userReviews = useSelector((state) => state.journal.userReviews) || [];
  const dateReviews = userReviews.filter(r => r.trade_date === selected_date);

  useEffect(() => {
    dispatch(getUserReviews());
  }, [dispatch]);

  // Gate CKEditor render until after first mount to avoid CKEditor watchdog errors
  const [editorCanMount, setEditorCanMount] = useState(false);
  useEffect(() => { setEditorCanMount(true); }, []);

  // Keep last good editor data to provide stable value during init
  const lastGoodDataRef = useRef('');

  // Additional gate: wait until auth is not loading and a short delay to avoid init during state churn
  const [editorRenderReady, setEditorRenderReady] = useState(false);
  useEffect(() => {
    if (!authLoading) {
      const t = setTimeout(() => setEditorRenderReady(true), 60);
      return () => clearTimeout(t);
    } else {
      setEditorRenderReady(false);
    }
  }, [authLoading, selected_date]);

  // Hold the editor instance ref
  const editorInstanceRef = useRef(null);

  // Container ref to ensure a real DOM node exists
  const editorContainerRef = useRef(null);
  

  const [backpageclick, setBackPageClick] = useState(false);
  const [nextpageclick, setNextPageClick] = useState(false);

  useEffect(() => {
    const todayDate = today;

    const applyFromLocal = () => {
      const savedJournalInfo = window.localStorage.getItem('journalInfo');
      if (savedJournalInfo) {
        const journalInfo = JSON.parse(savedJournalInfo);
        let appliedDate = null;
        if (journalInfo.selectedDate) {
          appliedDate = journalInfo.selectedDate;
          setSelectedDate(appliedDate);
        } else if (journalInfo.date) {
          appliedDate = journalInfo.date;
          setSelectedDate(appliedDate);
        }
        if (appliedDate) {
          setCurrentMonth(moment(appliedDate).month());
          setCurrentYear(moment(appliedDate).year());
        }
        if (journalInfo.entry && journalInfo.source === 'manual') {
          setEntry(journalInfo.entry || "");
        }
        window.localStorage.removeItem('journalInfo');
        return true;
      }
      return false;
    };

    // Try localStorage first
    if (!applyFromLocal()) {
      setSelectedDate(todayDate);
      setCurrentMonth(moment(todayDate).month());
      setCurrentYear(moment(todayDate).year());
    }

    // Listen for navbar-triggered navigation events to update date live
    const handleJournalNavigate = (e) => {
      const { selectedDate } = e.detail || {};
      if (selectedDate) {
        setSelectedDate(selectedDate);
        setCurrentMonth(moment(selectedDate).month());
        setCurrentYear(moment(selectedDate).year());
      }
    };
    window.addEventListener('journalNavigate', handleJournalNavigate);

    return () => {
      window.removeEventListener('journalNavigate', handleJournalNavigate);
    };
  }, []);

  useEffect(() => {
    evaluateDateArrows();
    // Clear any stale manual entry when date changes
    setEntry('');
    setIsEdit(false);
    window.localStorage.removeItem('journalInfo');
  }, [selected_date]);


  useEffect(() => {
    //Check for if journal entry exists yet that matches entryy otherwise put into edit mode
    if (entryy !== ''){
      const entry = entryy;
      const date = selected_date;
      const journalInfo = {
        selectedDate: date,  // Use new structure
        entry,
        source: 'manual',    // Indicate this is from manual entry
        lastApiCall: new Date().toISOString()
      };
      window.localStorage.setItem('journalInfo', JSON.stringify(journalInfo));
    }
    if (hasEntries) {
      const existingentry = journalentries.entries.find(entry => entry.date === selected_date)?.entrytext || '';
      if (existingentry !== entryy && entryy !== ''){
        setIsEdit(true);
      }
    }
    
    // This should basically mean if you were previously editing and saving didnt work put back into edit and load entryy text
    // Right now it is doing some checks in the WYSIWYG editor but because journal entry object is empty for that day it is loading entryy text but not in edit mode, and when I edit it is blank
  }, [entryy,hasEntries]);
  

  const evaluateDateArrows = async () => {
    if(selected_date === today){
      setNextPageEnable(false);
    }
    else{
      setNextPageEnable(true);
    }

    if(selected_date === "1900-01-01"){
      setBackPageEnable(false);
    }else{
      setBackPageEnable(true);
    }

    const date = selected_date;
    if(moment(selected_date).date() === 1){
      if(nextpageclick === true){
        await dispatch(getJournalEntries({ date })); 
      }else if(backpageclick === true) {
      }
    }else if (moment(selected_date).add(1, 'days').date() === 1){
      if(nextpageclick === true){
      }else if(backpageclick === true) {
        await dispatch(getJournalEntries({ date }));
      }
    }
    setNextPageClick(false);
    setBackPageClick(false);

    //Need to add condition when user selects from date component
    if(moment(selected_date).month() !== currentmonth || moment(selected_date).year() !== currentyear){
      await dispatch(getJournalEntries({ date }));
      setCurrentMonth(moment(selected_date).month());
      setCurrentYear(moment(selected_date).year());
    }
  }

  const handleNextPage = () => {
    if(nextPageEnable){
      setEntry('');
      window.localStorage.removeItem('journalInfo');
      setIsEdit(false);
      setNextPageClick(true);
      setSelectedDate(moment(selected_date).add(1, 'days').format('YYYY-MM-DD'));
    }
  }

  const handleBackPage = () => {
    if(backPageEnable){
      setEntry('');
      window.localStorage.removeItem('journalInfo');
      setIsEdit(false);
      setBackPageClick(true);
      setSelectedDate(moment(selected_date).add(-1, 'days').format('YYYY-MM-DD'));
    }
  }

  useEffect(() => {
    evaluateSuccess();
  }, [success]); 


  const evaluateSuccess = async () => {
    const date = selected_date;
    if(success === true && info && info.result && info.result === "Journal Entry Successfully Deleted"){
      setIsEdit(false);
      await dispatch(getJournalEntries({ date }));
      window.localStorage.removeItem('journalInfo');
      showToast(info.result);
    }else if (success === true && info && info.result && info.result === "Journal Entry Successfully Saved"){
      setIsEdit(false);
      await dispatch(getJournalEntries({ date }));
      window.localStorage.removeItem('journalInfo');
      showToast(info.result);
    }else if (success === true && info && info.result && info.result === "Journal Entry Successfully Updated"){
      setIsEdit(false);
      await dispatch(getJournalEntries({ date }));
      window.localStorage.removeItem('journalInfo');
      showToast(info.result);
    }else if (success === true && info && info.result && info.result === "Journal Entry Successfully Deleted"){
      setIsEdit(false);
      await dispatch(getJournalEntries({ date }));
      window.localStorage.removeItem('journalInfo');
      showToast(info.result);
    }
  }
  const handleCancel = () => {
    setEntry('');
    window.localStorage.removeItem('journalInfo');
    setIsEdit(false);
  };

  const handleSave = async () => {
    const entry = entryy
    const date = selected_date;
    const existingentry = journalentries.entries.find(entry => entry.date === selected_date)?.entrytext || '';
    if(entry === "" && existingentry !== ''){
      setSaveLoading(true);
      await dispatch(clearJournalEntry({ date }));
      setSaveLoading(false);
    }else if (entry !== ""){
      setSaveLoading(true);
      await dispatch(postJournalEntry({ date, entry }));
      setSaveLoading(false);
    }else{
      showToast("Please enter information before trying to Save", { status: "error" });
    }
  };

  const handleGotoEdit = (e) => {
    e.preventDefault();
    if(!isEdit){
      setEntry(journalentries.entries.find(entry => entry.date === selected_date)?.entrytext || '');
      window.localStorage.removeItem('journalInfo');
      setIsEdit(true);
    }
  };

  const handleClearEntry = (e) => {
    e.preventDefault();
    setEntry('');
    window.localStorage.removeItem('journalInfo');
    setIsEdit(true);
  };


  const handleEntryDisplay = () => {
    let content = [];
    // Defer editor mount on initial paint to avoid CKEditor watchdog errors
    if (!editorCanMount || !editorRenderReady || authLoading) {
      content.push(
        <div class={`paper-content ${colorMode === 'light' ? 'paper-content-light' : 'paper-content-dark'}`}>
          <Center>
            <Spinner
              thickness='2px'
              speed='0.65s'
              emptyColor='gray.200'
              color='blue.500'
              size='md'
            />
          </Center>
        </div>
      );
      return content;
    }

    // Always provide CKEditor a safe, non-null string
    const editorData = (hasEntries && !isEdit)
      ? (journalentries?.entries?.find(entry => entry.date === selected_date)?.entrytext ?? '')
      : (entryy ?? '');
      // Update last good data
      lastGoodDataRef.current = typeof editorData === 'string' ? editorData : '';

    content.push(
      <div ref={editorContainerRef} class={`paper-content ${colorMode === 'light' ? 'paper-content-light' : 'paper-content-dark'}`}>
        <CKEditor
          editor={Editor}
          data={lastGoodDataRef.current}
          disabled={!isEdit}
          onReady={(editor) => {
            editorInstanceRef.current = editor;
            // Wrap destroy to prevent errors when CKEditor's React component
            // cleans up after the DOM root element has been removed during navigation
            const originalDestroy = editor.destroy.bind(editor);
            editor.destroy = () => {
              try {
                return originalDestroy().catch(() => {});
              } catch (e) {
                return Promise.resolve();
              }
            };
          }}
          onError={(error) => {
            // Prevent noisy init errors from breaking the UI
            console.warn('CKEditor init error:', error);
          }}
          onChange={(event, editor) => {
            const data = editor.getData();
            setEntry(data);
            lastGoodDataRef.current = data ?? '';
          }}
        />
      </div>
    );
    return content
  };


  // grabbing current date to set a max to the birthday input
  const currentDate = new Date();
  let [month, day, year] = currentDate.toLocaleDateString().split("/");
  // input max field must have 08 instead of 8
  month = month.length === 2 ? month : "0" + month;
  day = day.length === 2 ? day : "0" + day;
  const maxDate = year + "-" + month + "-" + day;        

  return (
      <Flex
        flexDirection="column"
        minHeight="100vh"
        backgroundColor={colorMode === 'light' ? "gray.200" : "gray.800"}
      >
        <Box flex="auto" overflowX="auto" overflowY="auto">
          {authLoading && !saveLoading ? (
            <VStack flex="auto" p={4} spacing={4} w="full" align="stretch" backgroundColor={colorMode === 'light' ? "gray.200" : "gray.800"}>
              <JournalSkeleton />
            </VStack>
          ) : (
            <VStack flex="auto" p={4} spacing={4} w="full" align="stretch" backgroundColor={colorMode === 'light' ? "gray.200" : "gray.800"}>
              {/* Save/Cancel Buttons */}
              <HStack spacing={3}>
                {authLoading && saveLoading ? (
                  <Button colorScheme="blue" size="sm" width="75px">
                    <Spinner thickness="2px" speed="0.65s" emptyColor="gray.200" color="white" size="sm" />
                  </Button>
                ) : (
                  <Button colorScheme="blue" size="sm" isDisabled={!isEdit} onClick={() => handleSave()}>
                    Save
                  </Button>
                )}
                <Button variant="outline" size="sm" isDisabled={!isEdit} onClick={() => handleCancel()}>
                  Cancel
                </Button>
              </HStack>

              {/* Navigation + Paper Box */}
              <HStack justifyContent="center" alignItems="center" w="100%">
                <IconButton
                  aria-label="Previous day"
                  icon={<ChevronLeftIcon boxSize={6} />}
                  variant="ghost"
                  borderRadius="full"
                  isDisabled={!backPageEnable}
                  onClick={handleBackPage}
                />
                <Box
                  bg={colorMode === 'light' ? 'white' : 'gray.700'}
                  boxShadow="md"
                  p={5}
                  mx="auto"
                  w="80%"
                  maxW="800px"
                  minH="70vh"
                  rounded="lg"
                >
                  <VStack spacing={0} align="stretch">
                    <HStack w="100%" justify="space-between" pb={4}>
                      <Button size="sm" variant="outline" onClick={e => handleClearEntry(e)}>
                        Clear
                      </Button>
                      <HStack spacing={2}>
                        <Input
                          maxWidth="200px"
                          size="sm"
                          type="date"
                          max={maxDate}
                          min="1900-01-01"
                          value={selected_date}
                          onChange={(e) => {
                            setSelectedDate(e.target.value);
                            window.localStorage.removeItem('journalInfo');
                          }}
                        />
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setSelectedDate(maxDate);
                            window.localStorage.removeItem('journalInfo');
                          }}
                          isDisabled={selected_date === maxDate}
                        >
                          Today
                        </Button>
                      </HStack>
                      <Button size="sm" variant="outline" onClick={e => handleGotoEdit(e)}>
                        Edit
                      </Button>
                    </HStack>
                    {handleEntryDisplay()}
                    {dateReviews.length > 0 && (
                      <Box mt={4} p={3} borderWidth="1px" borderRadius="md" bg={colorMode === 'light' ? 'blue.50' : 'gray.600'} w="100%">
                        <Text fontWeight="bold" fontSize="sm" mb={2}>Trade Reviews for {selected_date}</Text>
                        {dateReviews.map((review, idx) => (
                          <Box key={idx} mb={2} p={2} borderWidth="1px" borderRadius="sm" bg={colorMode === 'light' ? 'white' : 'gray.700'} position="relative">
                            {review.screenshot_url && (
                              <Tooltip label="View screenshot" fontSize="xs" hasArrow>
                                <IconButton
                                  icon={<FaImage />}
                                  size="xs"
                                  variant="ghost"
                                  aria-label="View screenshot"
                                  position="absolute"
                                  top={1}
                                  right={1}
                                  onClick={() => setScreenshotModalUrl(review.screenshot_url)}
                                />
                              </Tooltip>
                            )}
                            <HStack spacing={3} flexWrap="wrap" pr={review.screenshot_url ? 6 : 0}>
                              <Text fontSize="xs" fontWeight="bold">{review.ticker_name}</Text>
                              {review.emotional_state && <Badge colorScheme={review.emotional_state === 'calm' ? 'green' : review.emotional_state === 'excited' ? 'yellow' : review.emotional_state === 'anxious' ? 'orange' : review.emotional_state === 'fearful' ? 'purple' : 'red'} fontSize="0.7em">{formatEmotionalState(review.emotional_state)}</Badge>}
                              {review.setup_type && <Text fontSize="xs">Setup: {review.setup_type}</Text>}
                              {review.followed_plan !== null && <Text fontSize="xs">Plan: {review.followed_plan ? 'Yes' : 'No'}</Text>}
                              {review.pnl !== null && <Text fontSize="xs" color={review.pnl >= 0 ? 'green.500' : 'red.500'}>P&L: {review.pnl}</Text>}
                            </HStack>
                            {review.notes && <Text fontSize="xs" mt={1} color={colorMode === 'light' ? 'gray.600' : 'gray.300'}>{review.notes}</Text>}
                          </Box>
                        ))}
                      </Box>
                    )}
                  </VStack>
                </Box>
                <IconButton
                  aria-label="Next day"
                  icon={<ChevronRightIcon boxSize={6} />}
                  variant="ghost"
                  borderRadius="full"
                  isDisabled={!nextPageEnable}
                  onClick={handleNextPage}
                />
              </HStack>
            </VStack>
          )}
        </Box>
        <Modal isOpen={!!screenshotModalUrl} onClose={() => setScreenshotModalUrl(null)} size="xl" isCentered>
          <ModalOverlay />
          <ModalContent>
            <ModalCloseButton />
            <ModalBody p={4}>
              <Image src={screenshotModalUrl} alt="Trade screenshot" w="100%" objectFit="contain" />
            </ModalBody>
          </ModalContent>
        </Modal>
      </Flex>
  )
}